/** @format */

// pages/ActuCfaPublic.jsx
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  Tag,
  User,
  Loader,
  ExternalLink,
  Home,
  Sparkles,
} from "lucide-react";
import { supabase } from "@lib/supabaseClient";

const ActuCfaPublic = () => {
  const [loading, setLoading] = useState(true);
  const [featuredActus, setFeaturedActus] = useState([]);
  const [allActus, setAllActus] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedCategorie, setSelectedCategorie] = useState("Toutes");
  const [selectedActu, setSelectedActu] = useState(null);

  const categories = [
    "Toutes",
    "Événement",
    "Formation",
    "Actualité",
    "Apprentissage",
    "MDA Maison des apprentis",
    "Vie du CFA",
  ];

  // Ajouter les styles pour l'animation du gradient
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes gradient {
        0%, 100% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
      }
      .animate-gradient {
        animation: gradient 3s ease infinite;
        background-size: 200% 200%;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Charger les actualités
  useEffect(() => {
    const fetchActualites = async () => {
      setLoading(true);
      try {
        // Actualités à la une
        const { data: featured } = await supabase
          .from("actu_cfa")
          .select(
            `
            *,
            profiles:auteur_id (
              first_name,
              last_name
            )
          `
          )
          .eq("is_featured", true)
          .order("date_publication", { ascending: false })
          .limit(5);

        // Toutes les actualités
        const { data: all } = await supabase
          .from("actu_cfa")
          .select(
            `
            *,
            profiles:auteur_id (
              first_name,
              last_name
            )
          `
          )
          .order("date_publication", { ascending: false });

        setFeaturedActus(featured || []);
        setAllActus(all || []);
      } catch (error) {
        console.error("Erreur:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchActualites();
  }, []);

  // Carrousel automatique
  useEffect(() => {
    if (featuredActus.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % featuredActus.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [featuredActus.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % featuredActus.length);
  };

  const prevSlide = () => {
    setCurrentSlide(
      (prev) => (prev - 1 + featuredActus.length) % featuredActus.length
    );
  };

  const filteredActus =
    selectedCategorie === "Toutes" ? allActus : (
      allActus.filter((actu) => actu.categorie === selectedCategorie)
    );

  const parseMarkdown = (text) => {
    if (!text) return "";

    // Échapper HTML
    let html = text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

    const lines = html.split("\n");
    const result = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i].trim();

      if (!line) {
        i++;
        continue;
      }

      // Titres (doivent être traités AVANT les paragraphes)
      if (/^# /.test(line)) {
        result.push(
          `<h1 class="text-3xl font-bold mt-10 mb-5 text-white">${line.replace(/^# /, "")}</h1>`
        );
        i++;
        continue;
      }
      if (/^## /.test(line)) {
        result.push(
          `<h2 class="text-2xl font-bold mt-8 mb-4 text-white">${line.replace(/^## /, "")}</h2>`
        );
        i++;
        continue;
      }
      if (/^### /.test(line)) {
        result.push(
          `<h3 class="text-xl font-bold mt-6 mb-3 text-white">${line.replace(/^### /, "")}</h3>`
        );
        i++;
        continue;
      }

      // Listes à puces
      if (/^[\-\*] /.test(line)) {
        const listItems = [];
        while (i < lines.length && /^[\-\*] /.test(lines[i].trim())) {
          const itemText = lines[i].trim().replace(/^[\-\*] /, "");
          listItems.push(
            `<li class="ml-4 text-gray-300">${formatInline(itemText)}</li>`
          );
          i++;
        }
        result.push(`<ul class="list-disc my-4">${listItems.join("")}</ul>`);
        continue;
      }

      // Listes numérotées
      if (/^\d+\. /.test(line)) {
        const listItems = [];
        while (i < lines.length && /^\d+\. /.test(lines[i].trim())) {
          const itemText = lines[i].trim().replace(/^\d+\. /, "");
          listItems.push(
            `<li class="ml-4 text-gray-300">${formatInline(itemText)}</li>`
          );
          i++;
        }
        result.push(`<ol class="list-decimal my-4">${listItems.join("")}</ol>`);
        continue;
      }

      // Citations
      if (/^> /.test(line)) {
        const quotes = [];
        while (i < lines.length && /^> /.test(lines[i].trim())) {
          quotes.push(formatInline(lines[i].trim().replace(/^> /, "")));
          i++;
        }
        result.push(
          `<blockquote class="border-l-4 border-green-500 pl-4 italic text-gray-300 my-4">${quotes.join("<br />")}</blockquote>`
        );
        continue;
      }

      // Ligne horizontale
      if (/^[\-\*_]{3,}$/.test(line)) {
        result.push('<hr class="my-6 border-slate-600" />');
        i++;
        continue;
      }

      // Paragraphes (tout le reste)
      const paragraphLines = [];
      while (
        i < lines.length &&
        lines[i].trim() &&
        !/^[#]{1,3} /.test(lines[i].trim()) &&
        !/^[\-\*] /.test(lines[i].trim()) &&
        !/^\d+\. /.test(lines[i].trim()) &&
        !/^> /.test(lines[i].trim()) &&
        !/^[\-\*_]{3,}$/.test(lines[i].trim())
      ) {
        paragraphLines.push(lines[i].trim());
        i++;
      }

      if (paragraphLines.length > 0) {
        const content = paragraphLines.join(" ");
        result.push(
          `<p class="mb-4 text-gray-300 leading-relaxed">${formatInline(content)}</p>`
        );
      }
    }

    return result.join("");
  };

  // Fonction helper pour formater les éléments inline (gras, italique, liens, etc.)
  const formatInline = (text) => {
    let formatted = text;

    // IMPORTANT: Traiter *** avant ** avant *
    // Images
    formatted = formatted.replace(
      /!\[([^\]]*)\]\(([^)]+)\)/g,
      '<img src="$2" alt="$1" class="rounded-lg my-4 max-w-full inline-block" />'
    );
    // Gras + Italique (***texte***)
    formatted = formatted.replace(
      /\*\*\*(.+?)\*\*\*/g,
      '<strong><em class="font-bold italic text-white">$1</em></strong>'
    );
    // Code inline
    formatted = formatted.replace(
      /`([^\`]+)`/g,
      '<code class="bg-slate-700 px-2 py-1 rounded text-green-400 text-sm font-mono">$1</code>'
    );
    // Gras (**texte**)
    formatted = formatted.replace(
      /\*\*(.+?)\*\*/g,
      '<strong class="font-bold text-white">$1</strong>'
    );
    // Italique (*texte*)
    formatted = formatted.replace(
      /\*(.+?)\*/g,
      '<em class="italic text-gray-300">$1</em>'
    );
    formatted = formatted.replace(
      /_(.+?)_/g,
      '<em class="italic text-gray-300">$1</em>'
    );
    // Liens
    formatted = formatted.replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="$2" class="text-blue-400 hover:text-blue-300 underline" target="_blank" rel="noopener noreferrer">$1</a>'
    );

    return formatted;
  };

  // Fonction pour nettoyer le markdown pour les aperçus (carrousel et cartes)
  const stripMarkdown = (text) => {
    if (!text) return "";

    return (
      text
        // Supprimer les images
        .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, "")
        // Supprimer les titres
        .replace(/^#{1,6}\s+/gm, "")
        // Supprimer les gras/italique/code
        .replace(/\*\*\*(.+?)\*\*\*/g, "$1")
        .replace(/\*\*(.+?)\*\*/g, "$1")
        .replace(/\*(.+?)\*/g, "$1")
        .replace(/_(.+?)_/g, "$1")
        .replace(/`([^`]+)`/g, "$1")
        // Supprimer les liens (garder le texte)
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1")
        // Supprimer les listes
        .replace(/^[\-\*\+]\s+/gm, "")
        .replace(/^\d+\.\s+/gm, "")
        // Supprimer les citations
        .replace(/^>\s+/gm, "")
        // Supprimer les lignes horizontales
        .replace(/^[\-\*_]{3,}$/gm, "")
        // Nettoyer les espaces multiples
        .replace(/\s+/g, " ")
        .trim()
    );
  };

  if (loading) {
    return (
      <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center'>
        <Loader className='w-12 h-12 animate-spin text-green-500' />
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
      {/* Header Hero moderne avec animations */}
      <div className='relative overflow-hidden bg-gradient-to-br from-purple-900 via-blue-900 to-slate-900'>
        {/* Fond animé avec cercles */}
        <div className='absolute inset-0 overflow-hidden'>
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 180, 360],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "linear",
            }}
            className='absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-purple-500/20 to-transparent rounded-full blur-3xl'
          />
          <motion.div
            animate={{
              scale: [1.2, 1, 1.2],
              rotate: [360, 180, 0],
            }}
            transition={{
              duration: 25,
              repeat: Infinity,
              ease: "linear",
            }}
            className='absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-to-tl from-blue-500/20 to-transparent rounded-full blur-3xl'
          />
        </div>

        <div className='relative max-w-7xl mx-auto px-4 py-8 md:py-12'>
          {/* Bouton Home */}
          <motion.a
            href='/'
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            whileHover={{ scale: 1.05, x: -5 }}
            whileTap={{ scale: 0.95 }}
            className='inline-flex items-center gap-2 mb-4 px-4 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 rounded-full text-white transition-all group'>
            <Home className='w-4 h-4 group-hover:-translate-x-1 transition-transform' />
            <span className='text-sm font-medium'>Retour à l'accueil</span>
          </motion.a>

          {/* Titre principal avec animation */}
          <div className='text-center space-y-4'>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className='flex items-center justify-center gap-2'>
              <motion.div
                animate={{
                  rotate: [0, 360],
                  scale: [1, 1.2, 1],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}>
                <Sparkles className='w-6 h-6 md:w-8 md:h-8 text-yellow-400' />
              </motion.div>

              <h1 className='text-3xl md:text-5xl lg:text-6xl font-black'>
                <span className='bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent animate-gradient'>
                  Actualités
                </span>
              </h1>

              <motion.div
                animate={{
                  rotate: [360, 0],
                  scale: [1, 1.2, 1],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}>
                <Sparkles className='w-6 h-6 md:w-8 md:h-8 text-yellow-400' />
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}>
              <p className='text-lg md:text-2xl font-bold text-white mb-2'>
                Suivez toute l'actualité de la
              </p>
              <motion.h2
                className='text-2xl md:text-4xl lg:text-5xl font-black bg-clip-text text-transparent'
                animate={{
                  backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
                }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "linear",
                }}
                style={{
                  backgroundImage:
                    "linear-gradient(90deg, #60a5fa, #a78bfa, #f472b6, #60a5fa)",
                  backgroundSize: "200% auto",
                }}>
                FACTORY By CFA Est Loiret
              </motion.h2>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className='text-gray-300 text-base md:text-lg max-w-3xl mx-auto'>
              Découvrez nos dernières actualités, événements et formations
            </motion.p>

            {/* Ligne de séparation animée */}
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 0.8, duration: 1 }}
              className='h-1 w-32 md:w-48 mx-auto rounded-full bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400'
            />
          </div>
        </div>

        {/* Vague en bas du header */}
        <div className='absolute bottom-0 left-0 right-0'>
          <svg
            viewBox='0 0 1440 120'
            className='w-full h-12 md:h-20'
            preserveAspectRatio='none'>
            <motion.path
              d='M0,64 C360,96 720,32 1080,64 C1440,96 1440,120 0,120 Z'
              fill='rgb(15, 23, 42)'
              initial={{
                d: "M0,64 C360,32 720,96 1080,64 C1440,32 1440,120 0,120 Z",
              }}
              animate={{
                d: [
                  "M0,64 C360,96 720,32 1080,64 C1440,96 1440,120 0,120 Z",
                  "M0,32 C360,64 720,96 1080,32 C1440,64 1440,120 0,120 Z",
                  "M0,64 C360,96 720,32 1080,64 C1440,96 1440,120 0,120 Z",
                ],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          </svg>
        </div>
      </div>

      {/* Carrousel des actualités à la une */}
      {featuredActus.length > 0 && (
        <div className='relative w-full h-[500px] md:h-[600px] overflow-hidden mt-4 md:mt-8'>
          <AnimatePresence mode='wait'>
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className='absolute inset-0'>
              <div className='relative w-full h-full'>
                {/* Image de fond */}
                <img
                  src={featuredActus[currentSlide]?.image_url}
                  alt={featuredActus[currentSlide]?.titre}
                  className='w-full h-full object-cover'
                />

                {/* Overlay gradient */}
                <div className='absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent' />

                {/* Contenu */}
                <div className='absolute bottom-0 left-0 right-0 p-8 md:p-12'>
                  <div className='max-w-7xl mx-auto'>
                    <motion.div
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.2 }}>
                      <div className='flex items-center gap-3 mb-4'>
                        <span className='px-3 py-1 bg-green-600 text-white text-sm rounded-full'>
                          {featuredActus[currentSlide]?.categorie}
                        </span>
                        <span className='text-gray-300 text-sm flex items-center gap-2'>
                          <Calendar className='w-4 h-4' />
                          {new Date(
                            featuredActus[currentSlide]?.date_publication
                          ).toLocaleDateString("fr-FR", {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                          })}
                        </span>
                      </div>

                      <h2 className='text-3xl md:text-5xl font-bold text-white mb-4 max-w-4xl'>
                        {featuredActus[currentSlide]?.titre}
                      </h2>

                      <p className='text-gray-300 text-lg max-w-3xl mb-6 line-clamp-2'>
                        {stripMarkdown(
                          featuredActus[currentSlide]?.contenu
                        )?.substring(0, 200)}
                        ...
                      </p>

                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() =>
                          setSelectedActu(featuredActus[currentSlide])
                        }
                        className='px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2'>
                        Lire la suite
                        <ExternalLink className='w-4 h-4' />
                      </motion.button>
                    </motion.div>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Contrôles du carrousel */}
          {featuredActus.length > 1 && (
            <>
              <button
                onClick={prevSlide}
                className='absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full transition-colors z-10'>
                <ChevronLeft className='w-6 h-6 text-white' />
              </button>

              <button
                onClick={nextSlide}
                className='absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full transition-colors z-10'>
                <ChevronRight className='w-6 h-6 text-white' />
              </button>

              {/* Indicateurs */}
              <div className='absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-10'>
                {featuredActus.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      index === currentSlide ? "bg-white w-8" : (
                        "bg-white/40 hover:bg-white/60"
                      )
                    }`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Filtres par catégorie */}
      <div className='max-w-7xl mx-auto px-4 py-10 md:py-14 mt-6 md:mt-10'>
        <div className='flex flex-wrap gap-3'>
          {categories.map((cat) => (
            <motion.button
              key={cat}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setSelectedCategorie(cat)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                selectedCategorie === cat ?
                  "bg-green-600 text-white"
                : "bg-slate-800 text-gray-300 hover:bg-slate-700"
              }`}>
              {cat}
            </motion.button>
          ))}
        </div>
      </div>

      {/* Grille d'actualités */}
      <div className='max-w-7xl mx-auto px-4 pb-20'>
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
          {filteredActus.map((actu, index) => (
            <motion.div
              key={actu.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -5 }}
              onClick={() => setSelectedActu(actu)}
              className='bg-slate-800/60 backdrop-blur-sm rounded-lg overflow-hidden border border-slate-700 hover:border-green-500 transition-all cursor-pointer group'>
              <div className='relative h-48 overflow-hidden'>
                <img
                  src={actu.image_url}
                  alt={actu.titre}
                  className='w-full h-full object-cover group-hover:scale-110 transition-transform duration-300'
                />
                <div className='absolute top-3 left-3'>
                  <span className='px-3 py-1 bg-green-600/90 backdrop-blur-sm text-white text-xs rounded-full'>
                    {actu.categorie}
                  </span>
                </div>
              </div>

              <div className='p-5'>
                <div className='flex items-center gap-2 text-xs text-gray-400 mb-3'>
                  <Calendar className='w-3 h-3' />
                  <span>
                    {new Date(actu.date_publication).toLocaleDateString(
                      "fr-FR",
                      {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      }
                    )}
                  </span>
                </div>

                <h3 className='text-lg font-bold text-white mb-2 line-clamp-2 group-hover:text-green-400 transition-colors'>
                  {actu.titre}
                </h3>

                <p className='text-sm text-gray-300 line-clamp-3 mb-4'>
                  {stripMarkdown(actu.contenu)?.substring(0, 150)}...
                </p>

                {actu.profiles && (
                  <div className='flex items-center gap-2 text-xs text-gray-400'>
                    <User className='w-3 h-3' />
                    <span>
                      {actu.profiles.first_name} {actu.profiles.last_name}
                    </span>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {filteredActus.length === 0 && (
          <div className='text-center py-20'>
            <p className='text-gray-400 text-lg'>
              Aucune actualité dans cette catégorie
            </p>
          </div>
        )}
      </div>

      {/* Modal détail actualité */}
      <AnimatePresence>
        {selectedActu && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className='fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4'
            onClick={() => setSelectedActu(null)}>
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className='bg-slate-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto'
              onClick={(e) => e.stopPropagation()}>
              <div className='relative h-64 md:h-96'>
                <img
                  src={selectedActu.image_url}
                  alt={selectedActu.titre}
                  className='w-full h-full object-cover'
                />
                <div className='absolute inset-0 bg-gradient-to-t from-slate-800 to-transparent' />

                <button
                  onClick={() => setSelectedActu(null)}
                  className='absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full transition-colors'>
                  <ChevronLeft className='w-6 h-6 text-white' />
                </button>
              </div>

              <div className='p-8'>
                <div className='flex items-center gap-3 mb-4'>
                  <span className='px-3 py-1 bg-green-600 text-white text-sm rounded-full'>
                    {selectedActu.categorie}
                  </span>
                  <span className='text-gray-400 text-sm flex items-center gap-2'>
                    <Calendar className='w-4 h-4' />
                    {new Date(selectedActu.date_publication).toLocaleDateString(
                      "fr-FR",
                      {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      }
                    )}
                  </span>
                </div>

                <h2 className='text-3xl md:text-4xl font-bold text-white mb-6'>
                  {selectedActu.titre}
                </h2>

                {selectedActu.profiles && (
                  <div className='flex items-center gap-2 text-sm text-gray-400 mb-8 pb-6 border-b border-slate-700'>
                    <User className='w-4 h-4' />
                    <span>
                      Par {selectedActu.profiles.first_name}{" "}
                      {selectedActu.profiles.last_name}
                    </span>
                  </div>
                )}

                <div
                  className='prose prose-invert max-w-none'
                  dangerouslySetInnerHTML={{
                    __html: parseMarkdown(selectedActu.contenu),
                  }}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ActuCfaPublic;
